import { createStore, useStore } from 'zustand'

/** Which inbox an ingested item belongs to. Comment and mention ids never
 *  collide, but the sets are kept apart so a pending mention can't keep the
 *  comments pill (or divider) alive, and vice versa. */
export type EngagementKind = 'comment' | 'mention'

/**
 * Cross-tree state for the "New {comments,mentions} available" pill, observed
 * by every mounted engagement list.
 *
 * The pill lives in the page header and each list owns its own coordination
 * store down in the panel, so they sit in different parts of the tree. This
 * module bridges that gap with two things:
 *
 * - `requestId`: bumped when the pill is clicked. Lists react by scrolling
 *   toward wherever the new items land.
 * - `pendingIds`: ids reported by the ingestion Pusher events, i.e. the items
 *   the user hasn't caught up with yet. A list marks an item as newly arrived
 *   when its id is in this set, then calls {@link markEngagementIdsSeen} to
 *   take it out. Identifying new items by id (rather than by diffing the list
 *   around a refetch) is what makes the boundary accurate under oldest-first
 *   sort, where new items land pages deep and can't be told apart from older
 *   items paged in behind them.
 *
 * A non-empty set therefore means "new items exist that the user hasn't
 * reached yet", which is what keeps the pill on screen while they scroll.
 */
type NewEngagementSignalState = {
  requestId: number
  pendingIds: Record<EngagementKind, ReadonlySet<string>>
  requestNewEngagements: () => void
  registerIngestedId: (kind: EngagementKind, id: string) => void
  markIdsSeen: (kind: EngagementKind, ids: readonly string[]) => void
  clearPendingIds: (kind: EngagementKind) => void
}

const EMPTY_IDS: ReadonlySet<string> = new Set()

const newEngagementSignalStore = createStore<NewEngagementSignalState>()(
  (set, get) => ({
    requestId: 0,
    pendingIds: { comment: EMPTY_IDS, mention: EMPTY_IDS },

    requestNewEngagements: (): void => set({ requestId: get().requestId + 1 }),

    registerIngestedId: (kind, id): void => {
      const current = get().pendingIds[kind]
      if (current.has(id)) return
      set({
        pendingIds: { ...get().pendingIds, [kind]: new Set([...current, id]) },
      })
    },

    markIdsSeen: (kind, ids): void => {
      const current = get().pendingIds[kind]
      if (current.size === 0 || ids.length === 0) return
      const next = new Set(current)
      for (const id of ids) next.delete(id)
      if (next.size === current.size) return
      set({ pendingIds: { ...get().pendingIds, [kind]: next } })
    },

    clearPendingIds: (kind): void => {
      if (get().pendingIds[kind].size === 0) return
      set({ pendingIds: { ...get().pendingIds, [kind]: EMPTY_IDS } })
    },
  }),
)

/** Bump the request counter, called by a pill right before it refetches. */
export const requestNewEngagements = (): void =>
  newEngagementSignalStore.getState().requestNewEngagements()

/** Record an id from an ingestion Pusher event as not yet caught up with. */
export const registerIngestedEngagementId = (
  kind: EngagementKind,
  id: string,
): void => newEngagementSignalStore.getState().registerIngestedId(kind, id)

/** Drop ids the user has now reached, so they stop counting as pending. */
export const markEngagementIdsSeen = (
  kind: EngagementKind,
  ids: readonly string[],
): void => newEngagementSignalStore.getState().markIdsSeen(kind, ids)

/** Forget everything pending, e.g. when the list identity changes. */
export const clearPendingEngagementIds = (kind: EngagementKind): void =>
  newEngagementSignalStore.getState().clearPendingIds(kind)

/** Read the ids the user hasn't caught up with, without subscribing. */
export const getPendingEngagementIds = (
  kind: EngagementKind,
): ReadonlySet<string> => newEngagementSignalStore.getState().pendingIds[kind]

/** Subscribe to the request counter (bumped when a pill is clicked). */
export const useNewEngagementsRequestId = (): number =>
  useStore(newEngagementSignalStore, (state) => state.requestId)

/** Subscribe to the ids the user hasn't caught up with yet. */
export const usePendingEngagementIds = (
  kind: EngagementKind,
): ReadonlySet<string> =>
  useStore(newEngagementSignalStore, (state) => state.pendingIds[kind])

/** Whether new items exist that the user hasn't reached yet. Keeps the pill
 *  on screen while they scroll toward items that are still pages away. */
export const useHasPendingEngagements = (kind: EngagementKind): boolean =>
  useStore(newEngagementSignalStore, (state) => state.pendingIds[kind].size > 0)
